import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import ChatModal from "../modals/ChatModal";
import MigrationChatModal from "../modals/MigrationChatModal";

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const POLL_INTERVAL = 30_000; // 30 seconds

export default function NotificationBell() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const canViewChat = hasPerm("chat.view");
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chatJoId, setChatJoId] = useState(null);
  const [chatMigId, setChatMigId] = useState(null);
  const bellRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread");
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    if (!canViewChat) return;
    setLoading(true);
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [canViewChat, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleItemClick = (item) => {
    setOpen(false);
    if (item.source === "migration") {
      setChatMigId(item.record_id);
    } else {
      setChatJoId(item.record_id);
    }
  };

  const handleChatClose = useCallback(() => {
    setChatJoId(null);
    setChatMigId(null);
    // Refresh notifications after closing chat (marks as read)
    fetchNotifications();
  }, [fetchNotifications]);

  if (!canViewChat) return null;

  return (
    <>
      <div className="relative" ref={bellRef}>
        <button
          onClick={() => setOpen((o) => !o)}
          className="relative p-1.5 rounded hover:bg-blue-600 active:bg-blue-800 transition-colors"
          aria-label="Notifications"
          title="Chat Notifications"
        >
          {/* Bell icon */}
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 leading-none">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>

        {open && (
          <>
            {/* Mobile overlay */}
            <div className="fixed inset-0 z-40 md:hidden" onClick={() => setOpen(false)} />
            <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white text-gray-900 rounded-lg shadow-xl overflow-hidden z-50 border border-gray-200">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Notifications</h3>
                {count > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setOpen(false); navigate("/messages"); }}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    title="Open Message Center"
                  >
                    {count} unread
                  </button>
                )}
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {loading && items.length === 0 ? (
                  <p className="text-center text-sm text-gray-400 py-8">Loading…</p>
                ) : items.length === 0 ? (
                  <div className="py-10 text-center">
                    <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <p className="text-sm text-gray-400">No unread notifications</p>
                  </div>
                ) : (
                  items.map((item, idx) => (
                    <button
                      key={`${item.source}-${item.record_id}`}
                      onClick={() => handleItemClick(item)}
                      className={`w-full text-left px-4 py-3 hover:bg-blue-50 active:bg-blue-100 transition-colors ${idx < items.length - 1 ? "border-b border-gray-100" : ""}`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Chat bubble icon */}
                        <div className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${item.source === "migration" ? "bg-purple-100" : "bg-blue-100"}`}>
                          <svg className={`w-4 h-4 ${item.source === "migration" ? "text-purple-600" : "text-blue-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${item.source === "migration" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                              {item.source === "migration" ? "Migration" : "NI"}
                            </span>
                            <span className="text-xs font-medium text-gray-900 truncate">{item.jo_number}</span>
                            <span className="ml-auto text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">{timeAgo(item.latest_at)}</span>
                          </div>
                          {item.customer_name && (
                            <p className="text-xs text-gray-600 truncate mt-0.5">{item.customer_name}</p>
                          )}
                          {item.latest_message && (
                            <p className="text-xs text-gray-400 truncate mt-0.5">
                              <span className="font-medium text-gray-500">{item.latest_message_by}:</span>{" "}
                              {item.latest_message}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
              {/* View all link */}
              <button
                onClick={() => { setOpen(false); navigate("/messages"); }}
                className="w-full px-4 py-2.5 text-center text-xs text-blue-600 hover:bg-blue-50 border-t border-gray-200 font-medium"
              >
                View all messages
              </button>
            </div>
          </>
        )}
      </div>

      {/* Chat modals - reusing existing components */}
      {chatJoId && (
        <ChatModal
          jobOrderId={chatJoId}
          onClose={handleChatClose}
        />
      )}
      {chatMigId && (
        <MigrationChatModal
          recordId={chatMigId}
          onClose={handleChatClose}
        />
      )}
    </>
  );
}
